import { useEffect, useState } from 'react';
import { ALL_MODULES, loadClinicModules, saveClinicModule } from './data.js';
import { friendlyError } from './lib/format.js';

export function ModulesPage({ identity, onModulesChanged }) {
  const [modules, setModules] = useState({});   // { moduleId: boolean }
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null); // moduleId sedang disimpan
  const [error, setError]       = useState('');

  // Hanya Admin / Super Admin yang bisa edit
  const roleName  = identity?.role?.role_name || '';
  const canEdit   = /^(admin|super\s*admin|admin\s*klinik|admin\s*utama)/i.test(roleName)
                 || identity?.appUser?.is_platform_admin;

  useEffect(() => {
    loadClinicModules()
      .then(setModules)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  }, []);

  async function toggle(moduleId, current) {
    if (!canEdit) return;
    setSaving(moduleId);
    setError('');
    try {
      await saveClinicModule(moduleId, !current);
      setModules(prev => ({ ...prev, [moduleId]: !current }));
      onModulesChanged?.();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSaving(null);
    }
  }

  const activeCount = Object.values(modules).filter(Boolean).length;

  return (
    <div className="page-stack">
      <article className="panel" style={{ padding: '1.5rem' }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Sistem</span>
            <h3>Modul Klinik</h3>
          </div>
          <span className="status status-positive">{activeCount} modul aktif</span>
        </div>

        <p style={{ fontSize: 13, color: '#6b6555', margin: '0 0 1.25rem' }}>
          Aktifkan modul layanan yang digunakan klinik Anda. Modul yang nonaktif tidak akan muncul
          di menu divisi, pendaftaran, dan pemeriksaan. Hanya Admin Utama yang dapat mengubah pengaturan ini.
        </p>

        {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

        {loading ? (
          <div className="content-loading"><div className="loader" /><span>Memuat modul…</span></div>
        ) : (
          <div className="mod-grid">
            {ALL_MODULES.map(mod => {
              const isActive = !!modules[mod.id];
              const isSaving = saving === mod.id;
              return (
                <div key={mod.id} className={`mod-card ${isActive ? 'mod-card-on' : 'mod-card-off'}`}>
                  <div className="mod-icon">{mod.icon}</div>
                  <div className="mod-body">
                    <div className="mod-name">{mod.name}</div>
                    <div className="mod-desc">{mod.desc}</div>
                    <div className="mod-division">Divisi: <code>{mod.division}</code></div>
                  </div>
                  <div className="mod-toggle-wrap">
                    <span className={`status ${isActive ? 'status-positive' : 'status-neutral'}`} style={{ fontSize: 11 }}>
                      {isActive ? 'Aktif' : 'Nonaktif'}
                    </span>
                    {canEdit ? (
                      <button
                        className={`mod-toggle ${isActive ? 'mod-toggle-on' : 'mod-toggle-off'}`}
                        onClick={() => toggle(mod.id, isActive)}
                        disabled={!!saving}
                        title={isActive ? 'Nonaktifkan modul' : 'Aktifkan modul'}
                      >
                        {isSaving ? '…' : isActive ? '■ Nonaktifkan' : '▶ Aktifkan'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>Hanya Admin</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </article>

      {!canEdit && (
        <div style={{ padding: '.75rem 1rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#854d0e' }}>
          ⚠ Hanya Admin Utama / Super Admin yang dapat mengaktifkan atau menonaktifkan modul.
        </div>
      )}

      <style>{`
        .mod-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }
        .mod-card {
          display: flex;
          gap: .75rem;
          padding: 1rem;
          border-radius: 12px;
          border: 2px solid #e5e7eb;
          background: #fff;
          transition: border-color .15s, background .15s;
        }
        .mod-card-on {
          border-color: #bbf7d0;
          background: #f0fdf4;
        }
        .mod-icon {
          font-size: 2rem;
          flex-shrink: 0;
          line-height: 1;
          padding-top: 2px;
        }
        .mod-body {
          flex: 1;
          min-width: 0;
        }
        .mod-name {
          font-size: 15px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 3px;
        }
        .mod-desc {
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 6px;
          line-height: 1.4;
        }
        .mod-division {
          font-size: 11px;
          color: #9ca3af;
        }
        .mod-division code {
          background: #f3f4f6;
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 10px;
          font-family: monospace;
          color: #374151;
        }
        .mod-toggle-wrap {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 8px;
          flex-shrink: 0;
        }
        .mod-toggle {
          font-size: 12px;
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          white-space: nowrap;
          transition: background .15s;
        }
        .mod-toggle-on {
          background: #fee2e2;
          color: #b91c1c;
        }
        .mod-toggle-on:hover { background: #fecaca; }
        .mod-toggle-off {
          background: #dcfce7;
          color: #166534;
        }
        .mod-toggle-off:hover { background: #bbf7d0; }
        .mod-toggle:disabled { opacity: .5; cursor: wait; }
      `}</style>
    </div>
  );
}
